// Rich text for marketing emails: the editor stores a small, safe subset of HTML (paragraphs, bold,
// italic, underline, strike, links, lists, coloured text). Everything else is stripped — tags we
// don't know lose their markup but keep their words; script/style-like tags lose their content too.
// Pure (no DOM, no Node APIs) so the browser editor, the canvas and the server render share it.

const ALLOWED = new Set(['p', 'br', 'strong', 'em', 'u', 's', 'a', 'ul', 'ol', 'li', 'span']);
const RENAME = { b: 'strong', i: 'em', strike: 's', del: 's', div: 'p', h1: 'p', h2: 'p', h3: 'p', h4: 'p', h5: 'p', h6: 'p', blockquote: 'p', font: 'span' };
const DROP_CONTENT = new Set(['script', 'style', 'iframe', 'object', 'embed', 'template', 'head', 'title', 'noscript', 'svg', 'math', 'select', 'textarea', 'button']);
const BLOCK = new Set(['p', 'ul', 'ol', 'li']);
const MAX_INPUT = 30000;

const escText = (s) => String(s).replace(/&(?!(?:[a-zA-Z][a-zA-Z0-9]{1,31}|#\d{1,7}|#x[0-9a-fA-F]{1,6});)/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const escAttr = (s) => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const decodeAttr = (s) =>
  String(s)
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');

function attrs(raw) {
  const out = {};
  const re = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*(?:=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;
  let m;
  while ((m = re.exec(raw || ''))) out[m[1].toLowerCase()] = decodeAttr(m[2] ?? m[3] ?? m[4] ?? '');
  return out;
}

const COLOR = /^(#[0-9a-fA-F]{3,8}|rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(,\s*[\d.]+\s*)?\))$/;
/** Only a text colour (and, for paragraphs, an alignment) survive from inline styles. */
function safeStyle(style, tag, color) {
  const out = [];
  for (const decl of String(style || '').split(';')) {
    const [k, ...v] = decl.split(':');
    const key = (k || '').trim().toLowerCase();
    const val = v.join(':').trim();
    if (key === 'color' && COLOR.test(val)) out.push(`color:${val}`);
    if (key === 'text-align' && (tag === 'p' || tag === 'li') && /^(left|center|right)$/.test(val)) out.push(`text-align:${val}`);
  }
  if (color && COLOR.test(color) && !out.some((d) => d.startsWith('color:'))) out.push(`color:${color}`);
  return out.join(';');
}

/** A link we're happy to put in an email: http(s), mailto, tel. */
export function safeHref(u) {
  const s = String(u || '').trim();
  if (/^(https?:\/\/|mailto:|tel:)/i.test(s)) return s;
  return null;
}

/**
 * Sanitize editor HTML to the allowed subset. `decorate(tag)` (optional) returns inline CSS added to
 * each output tag — the email renderer uses it to style paragraphs, links and lists for mail clients.
 */
export function sanitizeRich(html, decorate) {
  const src = String(html ?? '').slice(0, MAX_INPUT);
  const re = /<!--[\s\S]*?-->|<\/?([a-zA-Z][a-zA-Z0-9]*)\b((?:[^>"']|"[^"]*"|'[^']*')*)>|[^<]+|</g;
  const stack = [];
  let out = '';
  let skip = null; // tag whose content we're dropping
  let m;
  const open = (tag, attrStr) => {
    const deco = decorate ? decorate(tag) : '';
    out += `<${tag}${attrStr}${deco ? ` style="${escAttr(deco)}"` : ''}>`;
  };
  const closeTo = (i) => {
    while (stack.length > i) out += `</${stack.pop()}>`;
  };
  while ((m = re.exec(src))) {
    const token = m[0];
    if (token.startsWith('<!--')) continue;
    if (!m[1]) {
      if (!skip) out += escText(token);
      continue;
    }
    const isClose = token.startsWith('</');
    const raw = m[1].toLowerCase();
    if (skip) {
      if (isClose && raw === skip) skip = null;
      continue;
    }
    if (DROP_CONTENT.has(raw)) {
      if (!isClose && !/\/\s*>$/.test(token)) skip = raw;
      continue;
    }
    const tag = RENAME[raw] || raw;
    if (!ALLOWED.has(tag)) continue;
    if (tag === 'br') {
      if (!isClose) out += '<br>';
      continue;
    }
    if (isClose) {
      const i = stack.lastIndexOf(tag);
      if (i >= 0) closeTo(i);
      continue;
    }
    // Paragraphs and lists don't nest inside paragraphs: close the open one first.
    if (BLOCK.has(tag) && tag !== 'li') {
      const pi = stack.lastIndexOf('p');
      if (pi >= 0) closeTo(pi);
    }
    if (tag === 'li' && !stack.some((t) => t === 'ul' || t === 'ol')) continue;
    const a = attrs(m[2]);
    let attrStr = '';
    if (tag === 'a') {
      const href = safeHref(a.href);
      if (!href) continue; // unsafe/empty link: keep the words, drop the link
      attrStr = ` href="${escAttr(href)}"`;
    }
    const deco = decorate ? decorate(tag) : '';
    const style = safeStyle(a.style, tag, a.color);
    if (style) {
      // Decoration first so the user's own colour / alignment wins.
      out += `<${tag}${attrStr} style="${escAttr([deco, style].filter(Boolean).join(';'))}">`;
      stack.push(tag);
      continue;
    }
    open(tag, attrStr);
    stack.push(tag);
  }
  closeTo(0);
  return out.replace(/<(strong|em|u|s|span)(?: style="[^"]*")?><\/\1>/g, '');
}

/** Plain text (legacy blocks, AI drafts) → rich HTML: blank line = new paragraph, newline = <br>. */
export function textToRich(text) {
  if (!String(text ?? '').trim()) return '';
  return String(text ?? '')
    .split(/\n{2,}/)
    .map((p) => `<p>${escText(p).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/** Rich HTML → plain text (the email's text part, word counts, checks). */
export function richToText(html) {
  return String(html ?? '')
    .replace(/<a [^>]*href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, (_, href, label) => {
      const l = label.replace(/<[^>]+>/g, '');
      return l.trim() && l.trim() !== href ? `${l} (${href})` : href;
    })
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<li[^>]*>/gi, '\n• ')
    .replace(/<\/(p|ul|ol)>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** The block's rich content, whether it was written in the new editor (html) or is legacy text. */
export const blockRich = (b) => (typeof b?.html === 'string' ? b.html : textToRich(b?.text || ''));
